/**
 * Patient pipeline-status API client. Reads GET /v1/patient/{id}/pipeline-status
 * (src/march/api/patient_pipeline_routes.py) so the physician workspace can show
 * "preparing…" sync states while the SMART-launched clockwork pipeline (twin +
 * initial risk heartbeat) runs in the background.
 */

import { fetchWithAuth } from './fetchWithAuth';
import { getMarchHttpApiBase, getMarchRuntimeBearer, isMarchHttpApiConfigured } from './marchApiEnv';

export type PatientPipelineStage = 'building_twin' | 'scoring' | 'ready';

/** One row of the SMART waiting-window step checklist (ticket #35/#36/#49).
 * `fhir_fetch` (#49) covers the network fetch from the clinical system --
 * previously the dominant, entirely untracked cost of the wait. The
 * `*_summary`/`*_synthesis`/`date_window_filter` ids are non-gating
 * background steps (ticket #39) -- see `gating` below. */
export type PipelineImportStepId =
  | 'fhir_fetch'
  | 'sdoh_transfer'
  | 'fhir_import'
  | 'reasoning_context'
  | 'patient_context_package'
  | 'date_window_filter'
  | 'trajectory_summary'
  | 'clinical_synthesis';
/**
 * Ticket #47 prerequisite: `skipped` is a FINAL, non-error status for a step
 * that legitimately did not run this launch (a dedup -- unchanged chart,
 * outputs already built). Distinct from `error`: nothing failed.
 */
export type PipelineImportStepStatus = 'running' | 'done' | 'error' | 'skipped';

export interface PipelineImportSubstep {
  resource: string;
  rows: number;
  success: boolean;
}

export interface PipelineImportStep {
  step_id: PipelineImportStepId;
  label: string;
  status: PipelineImportStepStatus;
  started_at: string | null;
  completed_at: string | null;
  elapsed_ms: number | null;
  error_short: string;
  /** Short human note -- populated on `skipped` (e.g. "Already up to date"),
   * empty otherwise. */
  note?: string;
  /** Full-sentence, clinician-facing caveat. Unlike `note` this is not
   * truncated server-side and is rendered as a warning, not a muted crumb. */
  warning?: string | null;
  substeps: PipelineImportSubstep[];
  /**
   * Ticket #39: which checklist section this step renders in (primary vs
   * background), and whether ITS OWN failure is a hard block or a soft
   * warning. Ticket #47 (2026-08-04): this no longer means "excluded from
   * the Continue gate" -- since that fix, Continue waits for EVERY step to
   * reach a final status regardless of this flag. Absent (older payloads /
   * the three original steps) means gating, so this stays backward
   * compatible with pre-#39 servers.
   */
  gating?: boolean;
}

/**
 * True once every GATING step (steps with `gating !== false`) has resolved
 * to `done` or `error` -- never while a gating step is still `running`.
 * Non-gating background steps are ignored entirely, in both directions: a
 * background step still `running` never holds this back, and a background
 * step already final never counts toward `minSteps`.
 *
 * Ticket #47 (2026-08-04): the Continue gate itself now lives entirely in
 * the server's `continue_gate` field (`waiting` until EVERY step, gating or
 * not, is final) -- this function is kept only as the fallback for a
 * degraded response with no `continue_gate` (an older server, or the
 * journal-read failure path, which reports `continue_gate: 'ready'` and an
 * empty `steps` array). Do not use this to compute the Continue gate when
 * `continue_gate` is present.
 *
 * `minSteps` lets each caller state its own "nothing to judge yet" floor.
 */
export function areStepsFinal(steps: PipelineImportStep[], minSteps = 1): boolean {
  const gatingSteps = steps.filter((step) => step.gating !== false);
  return gatingSteps.length >= minSteps && gatingSteps.every((step) => step.status !== 'running');
}

/**
 * Server-derived Continue-button gate for the checklist (ticket #36,
 * widened by #47): `waiting` while ANY step in the journal's full step
 * order has not yet reached a final status (done/error/skipped) — Continue
 * is not offered yet, full stop; `hard_block` when the FHIR import itself
 * (or any one of its resource fetches) failed — no Continue; `soft_warning`
 * when some OTHER step ended in error — Continue still shown with a
 * data-gap notice; `ready` when every step is final and clean. The rule
 * lives ONLY on the backend (`_derive_continue_gate`); the frontend must
 * not re-derive it from `steps` itself.
 */
export type PipelineContinueGate = 'waiting' | 'ready' | 'soft_warning' | 'hard_block';

/**
 * Pipeline readiness for the organ-based digital twin. The condition-based
 * risk-heartbeat/alert extras were retired (docs/CONDITION_RISK_TEARDOWN_PLAN.md);
 * the backend (`_compose_status`) now emits only the two clockwork checks —
 * twin (seeded from EHR) and the whole-patient overall summary — plus optional
 * trajectory readiness. Sub-objects are optional-safe on the client so a
 * degraded/partial payload can never crash a consumer.
 */
export interface PatientPipelineStatus {
  patient_id?: string;
  stage: PatientPipelineStage;
  ready: boolean;
  twin?: { ready: boolean };
  /** Deterministic Organ Health scores — the fast readiness signal. */
  organs?: { ready: boolean };
  /** Background LLM narrative (Trajectory v2); non-blocking, may lag readiness. */
  summary?: { ready: boolean };
  last_refresh?: string | null;
  /**
   * Trajectory readiness. Optional + forward-compatible: absent/​disabled reads
   * as "unavailable" rather than failed.
   */
  trajectory?: { available: boolean; ready: boolean };
  /** SMART waiting-window step checklist (ticket #35/#36). Absent/empty on an
   * environment or session where the step journal could not be resolved --
   * the checklist UI must degrade to its pre-#36 behavior in that case. */
  steps?: PipelineImportStep[];
  continue_gate?: PipelineContinueGate;
}

function headers(): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  const key = (import.meta.env.VITE_COMMAND_API_KEY ?? '').trim();
  if (key) h['X-API-Key'] = key;
  const bearer = getMarchRuntimeBearer();
  if (bearer) h.Authorization = `Bearer ${bearer}`;
  return h;
}

export function isPatientPipelineApiAvailable(): boolean {
  return isMarchHttpApiConfigured();
}

export interface PreparePatientPipelineResult {
  started: boolean;
  tenant_id: string;
  /** Durable Job Center job id when the job system is configured (track live
   * progress there); null when the prepare ran as an inline background task. */
  job_id?: string | null;
  status: PatientPipelineStatus;
}

/**
 * Admin/operator action: run or re-run the clockwork pipeline (twin + initial
 * heartbeat) for a patient on demand. Schedules the work in the background and
 * returns the current status to poll from. Requires admin/write access
 * server-side (POST /v1/admin/patient/{id}/prepare).
 */
/**
 * Enqueue ONE full prepare for every registered patient (the sweep dispatcher:
 * one child job per patient, draining serially behind the heavy-lane cap).
 * Admin-only; 503 when the durable job store is absent -- a fleet run never
 * executes in-process.
 */
export async function prepareAllPatients(tenantId?: string): Promise<{ job_id: string | null }> {
  const qs = tenantId?.trim() ? `?tenant_id=${encodeURIComponent(tenantId.trim())}` : '';
  const res = await fetchWithAuth(`${getMarchHttpApiBase()}/v1/admin/patient/fleet/prepare${qs}`, {
    method: 'POST',
    extraHeaders: headers(),
  });
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(detail || `fleet prepare failed (${res.status})`);
  }
  return (await res.json()) as { job_id: string | null };
}

export async function preparePatientPipeline(
  patientId: string,
  tenantId?: string,
): Promise<PreparePatientPipelineResult> {
  const trimmed = patientId.trim();
  const qs = tenantId?.trim() ? `?tenant_id=${encodeURIComponent(tenantId.trim())}` : '';
  const res = await fetchWithAuth(
    `${getMarchHttpApiBase()}/v1/admin/patient/${encodeURIComponent(trimmed)}/prepare${qs}`,
    { method: 'POST', extraHeaders: headers() },
  );
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(detail || `prepare request failed (${res.status})`);
  }
  return (await res.json()) as PreparePatientPipelineResult;
}

/**
 * Fetch the current pipeline readiness for a patient. Returns null when the API
 * isn't configured or the patient isn't found (treated as "not ready yet", not a
 * fault) so the workspace degrades to a calm preparing state.
 */
export async function fetchPatientPipelineStatus(patientId: string): Promise<PatientPipelineStatus | null> {
  const trimmed = patientId.trim();
  if (!trimmed || !isMarchHttpApiConfigured()) return null;
  const res = await fetchWithAuth(
    `${getMarchHttpApiBase()}/v1/patient/${encodeURIComponent(trimmed)}/pipeline-status`,
    { method: 'GET', extraHeaders: headers() },
  );
  if (res.status === 404) return null;
  if (!res.ok) {
    const detail = await res.text().catch(() => '');
    throw new Error(detail || `pipeline-status request failed (${res.status})`);
  }
  const text = await res.text();
  return text ? (JSON.parse(text) as PatientPipelineStatus) : null;
}
